import type { MouseEvent } from 'react';
import { useCallback, useState } from 'react';

import { useFramework } from '@suntex.fim/framework';
import { showErrorMessage } from '@suntex.fim/ui';

import { downloadCertificate } from '../download-certificate';

/**
 * Returns a click handler that intercepts clicks on `.download-certificate`
 * links to fetch, fill, and download the certificate PDF.
 */
export function useCertificateDownload() {
  const { api } = useFramework();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const target = (event.target as HTMLElement).closest<HTMLAnchorElement>(
        'a.download-certificate',
      );
      if (!target || isDownloading) return;

      event.preventDefault();

      const certLink = target.getAttribute('certificate');
      const playerIdStr = target.getAttribute('playerid');

      if (!certLink || !playerIdStr) return;

      const playerId = parseInt(playerIdStr, 10);
      if (Number.isNaN(playerId)) return;

      setIsDownloading(true);
      void downloadCertificate(api, certLink, playerId)
        .catch((error: unknown) => {
          showErrorMessage('Unable to download certificate', error);
        })
        .finally(() => {
          setIsDownloading(false);
        });
    },
    [api, isDownloading],
  );

  return { handleClick, isDownloading };
}
